import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router";
import { termsKey } from "@/hooks/query-keys";
import { readTerms } from "@/services/terms";

/**
 * The terms of a draft, read again whenever they may have moved.
 *
 * Any record of the draft may name a term (a figure's part, an element, a
 * position), so no one edit's invalidation can be relied on to reach them.
 * Instead the terms are read again after any edit that lands, and when the
 * attorney moves to another page, where a stage reached for the first time may
 * bring its group with it.
 */
export function useTerms(taskId: string, enabled: boolean) {
  const client = useQueryClient();
  const { pathname } = useLocation();
  const lastPathRef = useRef(pathname);

  useEffect(() => {
    if (!enabled) return undefined;
    return client.getMutationCache().subscribe((event) => {
      if (event.type === "updated" && event.action.type === "success") {
        void client.invalidateQueries({ queryKey: termsKey(taskId) });
      }
    });
  }, [client, taskId, enabled]);

  useEffect(() => {
    if (lastPathRef.current === pathname) return;
    lastPathRef.current = pathname;
    if (enabled) void client.invalidateQueries({ queryKey: termsKey(taskId) });
  }, [client, taskId, enabled, pathname]);

  return useQuery({
    queryKey: termsKey(taskId),
    queryFn: () => readTerms(taskId),
    enabled,
  });
}
